import { defineStore } from "pinia";
import { useKy } from "boot/ky";
import { LocalStorage } from "quasar";
import { ssrSafeLocalStorage } from "src/utils/ssrSafeStorage";
import { useAuthUnifiedStore } from "./authUnified.js";
import SessionStorageService from "src/services/sessionStorageService";
import { LogementService } from "src/services/LogementService";

// ✅ Exporter les champs de base pour les réutiliser dans d'autres stores (ex: favoris)
export const baseLogementFields = [
  "id",
  "libelle",
  "place_name",
  "nombre_de_lits",
  "city_name",
  "note",
  "nombre_de_salles_de_bain",
  "nombre_de_chambres",
  "tarif",
  "type_de_logements.libelle",
  "photos.directus_files_id",
  "display_name",
  "liste_de_reduction.type_de_reduction_id.*",
  "liste_de_reduction.mode_de_saisie",
  "liste_de_reduction.poucentage_de_reduction",
  "liste_de_reduction.prix_reduit_fixe",
  "is_verified_by_awlo",
  "geolocalisation",
];

// 🔧 Configuration globale du store pour performance optimale
const STORE_CONFIG = {
  DEBOUNCE_MS: 150, // Debounce pour éviter les requêtes spam
  RETRY_ATTEMPTS: 3, // Nombre de tentatives en cas d'échec
  RETRY_DELAY: 1000, // Délai entre les tentatives (ms)
  REQUEST_TIMEOUT: 30000, // Timeout des requêtes (30s)
};

// 🎯 Utilitaires de performance
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const useLogementsStore = defineStore("logements", {
  state: () => ({
    /** @type {Array<Object>} Liste des logements actuellement chargés. */
    items: [],
    /** @type {Array<Object>} Logements chargés en mode PUBLIC (persistés). */
    publicItems: [],
    /** @type {Array<Object>} Logements chargés en mode AUTH (persistés). */
    authItems: [],
    /** @type {Object | null} Logement actuellement chargé. */
    item: null,
    /** @type {boolean} Indicateur de chargement pour les opérations asynchrones. */
    inProgress: false,
    /** @type {number} Page actuelle pour la pagination. */
    page: 1,
    /** @type {number} Nombre d'éléments à récupérer par page. */
    limit: 12,
    /** @type {Object | null} Logement temporaire pour navigation. */
    tempLogement: null,
    /** @type {boolean} Indicateur de recherche en cours. */
    searchIsActive: false,
    /** @type {number} Nombre total de logements correspondant aux filtres actuels. */
    totalCount: 0,
    /** @type {number} Total PUBLIC (persisté). */
    publicTotalCount: 0,
    /** @type {number} Total AUTH (persisté). */
    authTotalCount: 0,
    /** @type {number} Nombre total de logements correspondant aux filtres actuels. */
    _totalCount: 0,
    /** @type {Object} Objet de filtres pour la requête API Directus. */
    filters: {},
    /** @type {Object} Objet de filtres pour la requête API Directus. */
    _filters: {
      status: { _eq: "published" },
      photos: { _nnull: true },
    },
    /** @type {string} Chaîne de tri pour la requête API Directus (ex: "-date_created"). */
    sort: "-is_verified_by_awlo,-awlo_rating,-note,-date_created",
    /** @type {string} Champs à récupérer de l'API, séparés par des virgules. */
    fields: "*",
    /** @type {Object | null} Paramètres "deep" pour requêter des données relationnelles imbriquées. */
    deep: null,
    /** @type {Object} Métadonnées retournées par l'API (ex: filter_count). */
    meta: {},
    /** @type {Object} Métadonnées PUBLIC (persistées). */
    publicMeta: {},
    /** @type {Object} Métadonnées AUTH (persistées). */
    authMeta: {},
    /** @type {string | number | null} ID du logement sélectionné pour un affichage détaillé. */
    selectedLogementId: null,
    /** @type {number | null} ID du type de logement activement sélectionné. */
    selectedLogementTypeId: 0,
    /** @type {Object | null} Objet Google Place complet pour la recherche géospatiale. */
    selectedPlace: null,
    /** @type {Object | null} Dates de séjour pour recherche géospatiale { from, to } */
    searchDates: null,
    /** @type {boolean} Indicateur de recherche en cours. */
    _searchResultIsVisible: false,
    // 🚀 Optimisations de performance
    /** @type {AbortController | null} Contrôleur d'annulation pour les requêtes. */
    _abortController: null,
    /** @type {number} Timestamp de la dernière requête pour éviter les doublons. */
    _lastRequestTime: 0,
    /** @type {boolean} Indicateur de première initialisation */
    _isInitialized: false,
    /** @type {Set<string>} Pool des IDs déjà chargés pour éviter les doublons */
    _loadedIds: new Set(),
    /** @type {Object} États de chargement granulaires */
    _loadingStates: {
      initialLoad: false,
      loadMore: false,
      refresh: false,
      search: false,
    },
    /** @type {boolean} Indicateur si les données actuelles sont publiques */
    _isPublic: false,
    /** @type {('public'|'auth')} Mode courant appliqué aux champs items/meta/totalCount utilisés par les vues. */
    currentMode: "public",
    /** @type {number} Timestamp du dernier chargement */
    _lastLoadTimestamp: null,
    /** @type {Array<Object>} Liste des catégories de logements (cache global) */
    categories: [],
    /** @type {number} Timestamp de la dernière récupération des catégories */
    _lastCategoriesFetch: 0,
  }),

  getters: {
    // 📋 Getters de base (optimisés avec memoization)
    /** @returns {Array<Object>} Retourne la liste des logements chargés. */
    getFilteredLogements: (state) => state.items || [],

    getFilteredLogementsCount: (state) => state._totalCount || 0,

    /** @returns {number} Calcule et retourne le nombre total de pages. */
    getTotalPages: (state) => {
      if (!state.totalCount || !state.limit || state.limit <= 0) return 0;
      return Math.ceil(state.totalCount / state.limit);
    },

    /** @returns {boolean} Retourne l'état de chargement en cours. */
    isLoading: (state) =>
      state.inProgress ||
      Object.values(state._loadingStates || {}).some(Boolean),

    /** @returns {boolean} Indique s'il y a d'autres pages à charger. */
    hasMoreItems: (state) => {
      if (!state.totalCount || !state.limit || state.limit <= 0) return false;
      return state.page < Math.ceil(state.totalCount / state.limit);
    },

    /** @returns {string | number | null} Retourne l'ID du logement actuellement sélectionné. */
    getSelectedLogementId: (state) => state.selectedLogementId,

    /** @returns {Object} Logement sélectionné */
    getSelectedLogement: (state) => {
      if (!state.selectedLogementId || !state.items) return null;
      return (
        state.items.find((item) => item.id === state.selectedLogementId) || null
      );
    },

    /** @returns {Object} Logements groupés par ville pour optimisation affichage */
    getLogementsByCity: (state) => {
      const grouped = {};
      if (!state.items || !Array.isArray(state.items)) return grouped;

      state.items.forEach((item) => {
        const city = item?.city_name || "Autre";
        if (!grouped[city]) grouped[city] = [];
        grouped[city].push(item);
      });
      return grouped;
    },

    /** @returns {Array<Object>} Logements triés par note pour recommandations */
    getTopRatedLogements: (state) => {
      if (!state.items || !Array.isArray(state.items)) return [];

      return state.items
        .filter((item) => item?.note && item.note > 0)
        .sort((a, b) => (b.note || 0) - (a.note || 0))
        .slice(0, 5);
    },

    /** @returns {Object} États de chargement détaillés */
    getLoadingStates: (state) => {
      try {
        return state._loadingStates || {};
      } catch (error) {
        return {};
      }
    },

    /** @returns {boolean} Vérifie si les données actuelles sont publiques */
    isPublicData: (state) => {
      // Vérifier si les données contiennent des filtres publics
      const hasPublicFilters =
        state.filters &&
        (state.filters.status === "published" ||
          state.filters._and?.some((filter) => filter.status === "published"));

      // Vérifier si on a le flag isPublic
      const hasPublicFlag = state._isPublic === true;

      return hasPublicFilters || hasPublicFlag;
    },
  },

  actions: {
    // ⚠️ IMPORTANT: Différenciation Cache Public vs Authentifié
    //
    // Les logements récupérés en mode PUBLIC peuvent être différents de ceux
    // récupérés en mode AUTHENTIFIÉ pour plusieurs raisons :
    //
    // 1. Permissions différentes : certains logements ne sont visibles qu'aux utilisateurs connectés
    // 2. Filtres appliqués : les requêtes publiques/privées peuvent avoir des filtres différents
    // 3. Données exposées : certains champs peuvent être cachés en mode public
    // 4. Favoris : disponibles uniquement pour les utilisateurs authentifiés
    //
    // Le cache est donc séparé en deux modes distincts pour éviter les incohérences :
    // - Cache 'public' : pour les requêtes sans token d'authentification
    // - Cache 'auth' : pour les requêtes avec token d'authentification
    //
    // Cette séparation garantit que les bonnes données sont affichées selon le contexte.

    // 🚀 MÉTHODES UTILITAIRES PRIVÉES OPTIMISÉES

    /**
     * Initialise les structures de données pour les optimisations
     * @private
     */
    _initializeOptimizations() {
      try {
        // Vérifier et réinitialiser les structures si nécessaire
        if (!this._loadedIds || typeof this._loadedIds.add !== "function") {
          this._loadedIds = new Set();
        }

        if (!this._isInitialized) {
          // S'assurer que _loadingStates est initialisé
          if (!this._loadingStates || typeof this._loadingStates !== "object") {
            this._loadingStates = {
              initialLoad: false,
              loadMore: false,
              refresh: false,
              search: false,
            };
          }

          this._isInitialized = true;
        }
      } catch (error) {
        // Initialisation minimale en cas d'erreur
        this._isInitialized = true;
      }
    },

    /**
     * Annule le controller d'annulation actuel de manière sécurisée
     * @private
     */
    _safeAbortController() {
      if (
        this._abortController &&
        this._abortController.abort &&
        !this._abortController.signal?.aborted
      ) {
        try {
          this._abortController.abort();
        } catch (error) {
          // Ignorer les erreurs d'annulation
        }
      }
    },

    /**
     * Valide les données avant traitement pour éviter les erreurs
     * @private
     * @param {Object} data - Données à valider
     * @returns {boolean} True si valide
     */
    _validateData(data) {
      return (
        data &&
        typeof data === "object" &&
        Array.isArray(data.data) &&
        data.data.every((item) => item && typeof item.id !== "undefined")
      );
    },

    /**
     * Normalise les filtres entrants pour Directus (ajout des opérateurs _eq)
     * @private
     * @param {Object} incomingFilters
     * @returns {Object}
     */
    _normalizeFilters(incomingFilters = {}) {
      try {
        const filters = JSON.parse(JSON.stringify(incomingFilters || {}));

        // type_de_logements.id: normalisation intelligente
        // - si déjà un opérateur (_in/_eq/...) -> ne pas re-envelopper
        // - si tableau -> { _in: [...] }
        // - sinon -> { _eq: value }
        if (
          filters?.type_de_logements &&
          typeof filters.type_de_logements === "object" &&
          typeof filters.type_de_logements.id !== "undefined"
        ) {
          const val = filters.type_de_logements.id;
          if (val && typeof val === "object") {
            const hasOperator = Object.keys(val).some((k) => k.startsWith("_"));
            if (!hasOperator) {
              if (Array.isArray(val)) {
                filters.type_de_logements.id = { _in: val };
              } else {
                filters.type_de_logements.id = { _eq: val };
              }
            }
          } else if (Array.isArray(val)) {
            filters.type_de_logements.id = { _in: val };
          } else {
            filters.type_de_logements.id = { _eq: val };
          }
        }

        return filters;
      } catch (e) {
        return incomingFilters || {};
      }
    },

    /**
     * Extrait les filtres applicables à une recherche géospatiale
     * (exclut _geospatial_search et geolocalisation, gérés par le service)
     * @private
     * @returns {Object|null} Filtres à passer à LogementService.searchByLocation
     */
    _getFiltersForGeospatialSearch() {
      const src = { ...this._filters, ...this.filters };
      delete src._geospatial_search;
      delete src.geolocalisation;
      if (!src || Object.keys(src).length === 0) return null;
      return this._normalizeFilters(src);
    },

    /**
     * Exécute une requête avec retry automatique et gestion d'erreurs optimisée
     * @private
     * @param {Function} requestFn - Fonction de requête à exécuter
     * @param {string} operation - Nom de l'opération (pour logging)
     * @returns {Promise<any>} Résultat de la requête
     */
    async _executeWithRetry(requestFn, operation) {
      let lastError = null;

      for (let attempt = 1; attempt <= STORE_CONFIG.RETRY_ATTEMPTS; attempt++) {
        try {
          const result = await Promise.race([
            requestFn(),
            new Promise((_, reject) =>
              setTimeout(
                () => reject(new Error("Request timeout")),
                STORE_CONFIG.REQUEST_TIMEOUT
              )
            ),
          ]);

          return result;
        } catch (error) {
          lastError = error;

          // Ne pas retry sur certaines erreurs
          if (
            error.response?.status === 401 ||
            error.response?.status === 403 ||
            error.response?.status === 404 ||
            error.name === "AbortError"
          ) {
            throw error;
          }

          // Attendre avant la prochaine tentative
          if (attempt < STORE_CONFIG.RETRY_ATTEMPTS) {
            await sleep(STORE_CONFIG.RETRY_DELAY * attempt);
          }
        }
      }

      throw lastError;
    },

    /**
     * Initialise la configuration de base du store.
     * @param {Array<string>} baseFields - La liste des champs de base à récupérer.
     */
    initializeConfig(baseFields, options = {}) {
      const photosLimit = options.photosLimit ?? 8;
      this.fields = baseFields.join(",");
      this.filters = {
        status: { _eq: "published" },
        photos: { _nnull: true },
      };
      this.deep = {
        photos: { _limit: photosLimit },
      };
    },

    /**
     * Améliore la configuration si l'utilisateur est un "Voyageur" connecté.
     * Ajoute la récupération des favoris de l'utilisateur.
     */
    enhanceConfigForVoyageur() {
      // ✅ Utiliser SessionStorageService pour vérifier l'authentification
      const user = SessionStorageService.getUser();
      if (SessionStorageService.hasToken() && user?.role?.name === "Voyageur") {
        this.deep.favoris = { _filter: { user_created: "$CURRENT_USER" } };
        this.fields += ",favoris";
      }
    },

    /**
     * Récupère la liste des logements depuis l'API
     * @param {Object} options - Options pour la récupération
     * @param {boolean} options.isPublic - Indique si c'est une requête publique ou authentifiée
     * @param {AbortSignal} options.signal - Signal d'annulation pour la requête
     * @param {boolean} options.forceServer - Forcer l'appel serveur (bypass cache HTTP)
     * @param {boolean} options.forceFresh - Forcer le rechargement depuis l'API (défaut: false)
     * @param {boolean} options.bypassGuard - Permet de bypass le guard quand appelé depuis loadMore
     * @returns {Promise<Object|null>} Les données récupérées ou null en cas d'erreur
     */
    async fetchLogements({
      isPublic,
      signal,
      forceServer = false,
      forceFresh = false,
      bypassGuard = false, // ✅ Permet de bypass le guard quand appelé depuis loadMore
    } = {}) {
      // 🚀 Initialisation et validations optimisées
      this._initializeOptimizations();
      // ✅ Déterminer le mode courant et l'appliquer sans changer les vues
      const mode = this._detectMode(isPublic); // 'public' | 'auth'
      const isPublicMode = mode === "public";
      this._isPublic = isPublicMode;

      // ⚠️ IMPORTANT : Ne pas réinitialiser items lors d'un loadMore (page > 1)
      // Sinon on écrase les items déjà chargés
      if (this.page === 1) {
        this._applyMode(mode);
      } else {
        // Pour loadMore, on garde les items actuels et on met juste à jour le mode
        this.currentMode = mode;
      }

      // Validation précoce stricte
      if (!this.fields || this.limit <= 0) {
        return null;
      }

      // 🔄 Déterminer le type de chargement
      const loadingType = this.page === 1 ? "initialLoad" : "loadMore";

      // ⚡ GUARD : Empêcher les appels simultanés (sauf si bypassGuard est true)
      if (!bypassGuard) {
        if (this.inProgress || this._loadingStates[loadingType]) {
          return null;
        }
      }

      // 🔄 Activer l'état de chargement
      this._loadingStates[loadingType] = true;
      this.inProgress = true;

      // Annuler la requête précédente si elle existe
      this._safeAbortController();

      // Créer un nouveau controller d'annulation
      this._abortController = signal ? { signal } : new AbortController();
      const requestSignal = this._abortController.signal;

      try {
        // 🚀 Exécution avec retry automatique et timeout
        const responseData = await this._executeWithRetry(async () => {
          return await LogementService.getAll({
            filters: this.filters,
            sort: this.sort,
            fields: this.fields,
            deep: this.deep,
            page: this.page,
            limit: this.limit,
            isPublic: isPublicMode,
            signal: requestSignal,
          });
        }, "fetchLogements");

        // 🔍 Validation stricte des données reçues
        if (!this._validateData(responseData)) {
          return null;
        }

        // 📊 Traitement optimisé
        const result = this._processResponseOptimized(
          responseData,
          isPublicMode
        );

        // Projeter et persister dans le bon conteneur sans changer les vues
        if (result) {
          this._storeResultForMode(mode, {
            data: this.items,
            meta: this.meta,
            totalCount: this.totalCount,
          });
        }

        return result;
      } catch (error) {
        return this._handleErrorOptimized(error);
      } finally {
        this.inProgress = false;
        this._loadingStates[loadingType] = false;
        this._abortController = null;

        this._loadingStates.loadMore = false;
      }
    },

    /**
     * Traite la réponse de l'API avec optimisations avancées
     * @private
     * @param {Object} responseData - Données de réponse
     * @param {boolean} isPublic - Type de requête (public/auth)
     * @returns {Object|null} Données traitées ou null
     */
    _processResponseOptimized(responseData, isPublic) {
      if (!responseData?.data) {
        this._resetState();
        return null;
      }

      // 📊 Traitement optimisé des données
      const newItems = responseData.data;
      const newMeta = responseData.meta || {};
      const newTotalCount = newMeta.filter_count || 0;

      // 🆔 Gestion intelligente des doublons avec Set pour performance O(1)
      const currentIds = new Set(this.items.map((item) => item.id));
      const uniqueNewItems = newItems.filter(
        (item) => !currentIds.has(item.id)
      );

      // Mise à jour de l'état selon le type de chargement
      if (this.page === 1) {
        // Premier chargement - remplacement complet
        this.items = newItems;
        this._loadedIds = new Set(newItems.map((item) => item.id));
      } else {
        // Chargement supplémentaire - ajout uniquement des nouveaux
        this.items = [...this.items, ...uniqueNewItems];
        uniqueNewItems.forEach((item) => this._loadedIds?.add(item.id));
      }

      this.meta = newMeta;
      this.totalCount = newTotalCount;

      // ✅ Mettre à jour le timestamp du dernier chargement
      this._lastLoadTimestamp = Date.now();

      return {
        data: this.items,
        meta: this.meta,
        totalCount: this.totalCount,
        newItemsCount: uniqueNewItems.length,
      };
    },

    /**
     * Traite la réponse de l'API de manière optimisée (méthode legacy)
     * @private
     * @param {Object} responseData - Données de réponse
     * @param {number} startTime - Timestamp de début pour mesurer la performance
     * @returns {Object|null} Données traitées ou null
     */
    _processResponse(responseData, startTime) {
      if (!responseData?.data) {
        this._resetState();
        return null;
      }

      // Mise à jour optimisée de l'état
      this.items = responseData.data;
      this.meta = responseData.meta || {};
      this.totalCount = responseData.meta?.filter_count || 0;

      return responseData;
    },

    /**
     * Gestion avancée des erreurs avec recovery automatique et métriques
     * @private
     * @param {Error} error - Erreur capturée
     * @returns {null} Toujours null en cas d'erreur
     */
    _handleErrorOptimized(error) {
      // Requête annulée - comportement normal, pas d'erreur
      if (error.name === "AbortError") {
        return null;
      }

      // 🛡️ Gestion intelligente selon le type d'erreur
      const status = error.response?.status;
      const isNetworkError = !status || status >= 500;
      const isClientError = status >= 400 && status < 500;

      // 📈 Messages d'erreur enrichis avec contexte
      const errorMessages = {
        401: "🔒 Token expiré - reconnexion requise",
        403: "🚫 Permissions insuffisantes",
        404: "🔍 Ressource introuvable",
        413: "📁 Requête trop volumineuse",
        429: "⏱️ Trop de requêtes - ralentir",
        500: "🔧 Erreur serveur Directus",
        502: "🌐 Problème de proxy/gateway",
        503: "🚧 Service temporairement indisponible",
        504: "⏰ Timeout du serveur",
        network: "🌐 Erreur de connexion réseau",
        timeout: "⏰ Timeout de la requête",
      };

      let errorType = "unknown";
      if (error.message?.includes("timeout")) {
        errorType = "timeout";
      } else if (isNetworkError) {
        errorType = "network";
      } else {
        errorType = status;
      }

      const message =
        errorMessages[errorType] || `❌ Erreur ${status || "inconnue"}`;

      // 🔄 Stratégie de recovery selon l'erreur
      if (isClientError && !this.items.length) {
        // Erreur client sans données - réinitialiser complètement
        this._resetState();
      } else if (isNetworkError && this.items.length > 0) {
        // Erreur réseau mais on a des données - les conserver
      } else {
        // Autres cas - réinitialisation partielle
        this._resetStatePartial();
      }

      return null;
    },

    /**
     * Gestion optimisée des erreurs avec codes de statut spécifiques (méthode legacy)
     * @private
     * @param {Error} error - Erreur capturée
     * @returns {null} Toujours null en cas d'erreur
     */
    _handleError(error) {
      // Requête annulée - comportement normal, pas d'erreur
      if (error.name === "AbortError") {
        return null;
      }

      this._resetState();

      // Gestion spécifique des erreurs Directus avec codes précis
      const status = error.response?.status;
      const errorMessages = {
        401: "🔒 Token expiré - reconnexion requise",
        403: "🚫 Permissions insuffisantes",
        429: "⏱️ Trop de requêtes - ralentir",
        500: "🔧 Erreur serveur Directus",
        502: "🌐 Problème de proxy/gateway",
        503: "🚧 Service temporairement indisponible",
        504: "⏰ Timeout du serveur",
      };

      const message =
        errorMessages[status] || `❌ Erreur ${status || "réseau"}`;
      return null;
    },

    /**
     * Réinitialisation complète optimisée de l'état
     * @private
     */
    _resetState() {
      // 🔧 S'assurer que les structures sont correctement initialisées
      this._initializeOptimizations();

      this.inProgress = false; // ✅ AJOUTER CETTE LIGNE
      this.items = [];
      this.meta = {};
      this.totalCount = 0;

      // Vérification de sécurité avant clear()
      if (this._loadedIds && typeof this._loadedIds.clear === "function") {
        this._loadedIds.clear();
      } else {
        // Réinitialiser si la structure est corrompue
        this._loadedIds = new Set();
      }

      // Réinitialiser les états de chargement
      Object.keys(this._loadingStates).forEach((key) => {
        this._loadingStates[key] = false;
      });
    },

    _resetFilters() {
      this._filters = {
        status: { _eq: "published" },
        photos: { _nnull: true },
      };
      this._totalCount = null;
    },

    /**
     * Réinitialisation partielle pour recovery après erreur
     * @private
     */
    _resetStatePartial() {
      // Conserver les items existants mais réinitialiser les métadonnées
      this.meta = {};
      // Ne pas toucher à totalCount pour permettre la pagination

      // Réinitialiser seulement les états de chargement
      Object.keys(this._loadingStates).forEach((key) => {
        this._loadingStates[key] = false;
      });
    },

    /**
     * Charge la page suivante de logements avec optimisations avancées
     * @param {AbortSignal} signal - Signal d'annulation optionnel
     * @param {boolean} isPublic - Type de requête (défaut: false = authentifié)
     * @returns {Promise<boolean>} True si des données ont été chargées
     */
    async loadMore({ signal, isPublic } = {}) {
      // 🔍 Vérifications précoces optimisées
      if (
        !this.hasMoreItems ||
        this._loadingStates.loadMore ||
        this.inProgress
      ) {
        return false;
      }

      this._initializeOptimizations();

      const mode = this._detectMode(isPublic); // Utiliser le paramètre isPublic si fourni
      // ⚠️ IMPORTANT : Ne pas réinitialiser items lors d'un loadMore
      // On garde les items existants et on met juste à jour le mode
      this.currentMode = mode;

      const currentPage = this.page;
      this.page++; // Augmenter la page avant l'appel
      this._loadingStates.loadMore = true;
      this.inProgress = true; // S'assurer que les deux flags sont actifs

      try {
        // La recherche géospatiale a sa propre logique de pagination, on la sépare
        if (this.filters._geospatial_search) {
          // NOTE: La logique de loadMore pour la recherche géospatiale doit être vérifiée séparément si elle est utilisée.
          // Pour l'instant, on se concentre sur le cas standard.
        }

        // 📄 Recherche standard
        const result = await this.fetchLogements({
          signal,
          forceFresh: true,
          bypassGuard: true, // Important pour bypasser le verrou dans fetchLogements
        });

        if (result && result.newItemsCount > 0) {
          return true;
        }

        // Si aucun nouvel item n'est chargé, on est probablement à la fin
        if (result && result.newItemsCount === 0) {
          this.page = currentPage; // On revient à la page précédente
        }
        return false;
      } catch (error) {
        this.page = currentPage; // Restaurer la page en cas d'erreur
        return false;
      } finally {
        this._loadingStates.loadMore = false;
        this.inProgress = false; // ✅ S'assurer de bien libérer les deux verrous
      }
    },

    /**
     * Ajoute de nouveaux éléments à la liste `items` existante.
     * @param {Array<Object>} newItems - Les nouveaux logements à ajouter.
     */
    appendItems(newItems) {
      this.items = [...this.items, ...newItems];
    },

    /**
     * Réinitialise le store avec options avancées
     * @param {Object} options - Options de réinitialisation
     * @param {boolean} options.resetFilters - Réinitialiser les filtres (défaut: true)
     * @param {boolean} options.resetPagination - Réinitialiser la pagination (défaut: true)
     */
    resetStore({
      resetFilters = true,
      resetPagination = true,
      force = false, // ⚠️ Nouveau paramètre pour forcer le reset
    } = {}) {
      // ⚠️ PROTECTION : Ne PAS vider le store si on a déjà des données valides
      if (!force && this.items.length > 0 && this.searchIsActive) {
        return;
      }

      // 🔧 S'assurer que les structures sont correctement initialisées
      this._initializeOptimizations();

      // 🛑 Annuler toutes les requêtes en cours
      this.cancelAllRequests();

      // 🔄 Réinitialisation de l'état principal
      this._resetState();

      // 📄 Gestion de la pagination
      if (resetPagination) {
        this.page = 1;
      }

      // 🔍 Gestion des filtres
      if (resetFilters) {
        this.filters = {};
      }
    },

    /**
     * Met à jour les filtres, réinitialise le store et lance une nouvelle recherche.
     * @param {Object} newFilters - Les nouveaux filtres à appliquer.
     */
    async updateFilters(newFilters) {
      this.filters = { ...this.filters, ...newFilters };
      this.page = 1;
      this.resetStore();
      return this.fetchLogements();
    },

    /**
     * Met à jour le critère de tri, réinitialise le store et lance une nouvelle recherche.
     * @param {string} newSort - La nouvelle chaîne de tri.
     */
    async updateSort(newSort) {
      this.sort = newSort;
      this.page = 1;
      this.resetStore();
      return this.fetchLogements();
    },

    /**
     * Lance une nouvelle recherche en mettant à jour les filtres, le tri et les relations.
     * @param {Object} searchParams - Les paramètres de recherche.
     * @param {Object} [searchParams.filters={}] - Les nouveaux filtres à appliquer.
     * @param {string} [searchParams.sort=null] - Le nouveau critère de tri.
     * @param {Object} [searchParams.deep=null] - Les nouveaux paramètres "deep".
     * @param {boolean} [searchParams.isPublic=false] - Type de requête.
     * @param {AbortSignal} [searchParams.signal] - Signal d'annulation.
     * @returns {Promise<Object|null>} Résultats de la recherche
     */
    async searchLogements({
      filters,
      sort,
      deep,
      isPublic = false,
      signal,
      forceServer = false,
    }) {
      this.searchIsActive = true;
      const mode = this._detectMode(isPublic);
      const isPublicMode = mode === "public";
      this._applyMode(mode);

      // Réinitialiser l'état
      this.page = 1;
      this._resetState();

      // Appliquer les nouveaux paramètres
      const baseFilters = {
        status: { _eq: "published" },
        photos: { _nnull: true },
      };

      if (filters) {
        const normalized = this._normalizeFilters(filters);
        this.filters = { ...baseFilters, ...normalized };
      }
      if (sort) this.sort = sort;
      if (deep) this.deep = deep;
      return this.fetchLogements({
        isPublic: isPublicMode,
        signal,
        forceServer,
      });
    },

    async searchFilteredLogements({
      filters,
      sort,
      deep,
      isPublic = false,
      signal,
    }) {
      //
      // Réinitialiser l'état
      this.page = 1;
      // this._resetState();

      // Appliquer les nouveaux paramètres

      if (sort) this.sort = sort;
      if (deep) this.deep = deep;
      this._filters = { ...this._filters, ...filters };

      const { get, getPublic } = useKy();
      const kyMethod = isPublic ? getPublic : get;
      const responseData = await kyMethod(`items/logements`, {
        searchParams: {
          filter: JSON.stringify(this._filters),
          page: this.page,
          aggregate: JSON.stringify({
            countDistinct: "id",
          }),
        },
      });

      this._totalCount = responseData.data[0].countDistinct.id;
      // return this.fetchLogements({ isPublic, signal });
    },
    /**
     * Rafraîchit la liste de logements en retournant à la première page et en rechargeant les données.
     * @param {Object} options - Options de rafraîchissement
     * @param {boolean} options.isPublic - Type de requête
     * @param {AbortSignal} options.signal - Signal d'annulation
     * @returns {Promise<Object|null>} Résultats du rafraîchissement
     */
    async refresh({ isPublic = false, signal } = {}) {
      const mode = this._detectMode(isPublic);
      const isPublicMode = mode === "public";
      this._applyMode(mode);

      this.cancelAllRequests();
      this.page = 1;
      this._resetState();

      return this.fetchLogements({ isPublic: isPublicMode, signal });
    },

    /**
     * Récupère un seul logement par son ID de manière optimisée.
     * @param {string | number} id - L'ID du logement à récupérer.
     * @param {Object} [options={}] - Les options de la requête.
     * @param {string} [options.fields] - Les champs à récupérer.
     * @param {Object} [options.deep] - Les relations à charger.
     * @param {AbortSignal} [options.signal] - Signal d'annulation.
     * @returns {Promise<Object|null>} Le logement trouvé ou null en cas d'erreur.
     */
    async getLogementById(id, { fields, deep, signal, isPublic = false } = {}) {
      // Validation précoce
      if (!id) {
        return null;
      }

      const startTime = performance.now();

      try {
        const searchParams = {
          fields: fields || this.fields,
        };

        // Sérialisation conditionnelle optimisée
        const deepToUse = deep || this.deep;
        if (deepToUse && Object.keys(deepToUse).length > 0) {
          searchParams.deep = JSON.stringify(deepToUse);
        }

        const { get, getPublic } = useKy();
        const mode = this._detectMode(isPublic);
        const isPublicMode = mode === "public";
        this._applyMode(mode);
        const kyMethod = isPublicMode ? getPublic : get;
        const responseData = await kyMethod(`items/logements/${id}`, {
          searchParams,
          signal,
        });

        this.item = responseData?.data || null;
        return responseData?.data || null;
      } catch (error) {
        if (error.name === "AbortError") {
          return null;
        }

        const status = error.response?.status;
        if (status === 404) {
        } else {
        }

        return null;
      }
    },

    /**
     * Annule toutes les requêtes en cours du store
     */
    cancelAllRequests() {
      this._safeAbortController();

      // Réinitialiser tous les états de chargement
      Object.keys(this._loadingStates).forEach((key) => {
        this._loadingStates[key] = false;
      });
    },

    /**
     * Méthode de debug pour diagnostiquer les problèmes de proxy
     */
    debugState() {
      try {
        const debug = {
          stateProperties: Object.keys(this),
          itemsLength: this.items?.length,
          isInitialized: this._isInitialized,
          hasLoadingStates: !!this._loadingStates,
        };

        return debug;
      } catch (error) {
        return { error: error.message };
      }
    },

    /**
     * Définit l'ID du logement actuellement sélectionné.
     * @param {string | number} id - L'ID du logement sélectionné.
     */
    setSelectedLogementId(id) {
      this.selectedLogementId = id;
    },

    /**
     * Définit l'ID du type de logement sélectionné.
     * @param {number} id - L'ID du type de logement.
     */
    setSelectedLogementType(id) {
      if (this.selectedLogementTypeId === id) {
        return;
      }
      this.selectedLogementTypeId = id;

      // ⚠️ NE PAS réinitialiser la pagination si un chargement est en cours !
      if (this._loadingStates.loadMore || this.inProgress) {
        return;
      }

      // Réinitialiser la pagination lors du changement de type de logement
      this.page = 1;
    },

    /**
     * Sauvegarde temporaire d'un logement pour navigation
     * @param {Object} logement - Logement à sauvegarder
     */
    saveTempLogement(logement) {
      if (!logement) {
        return;
      }
      this.tempLogement = { ...logement }; // Créer une copie pour éviter les références
    },

    /**
     * Récupère le logement temporaire (sans le nettoyer)
     * @returns {Object|null} Logement temporaire ou null
     */
    getTempLogement() {
      return this.tempLogement;
    },

    /**
     * Nettoie le logement temporaire
     */
    clearTempLogement() {
      this.tempLogement = null;
    },

    /**
     * Vérifie si un logement temporaire existe
     * @returns {boolean} True si tempLogement existe
     */
    hasTempLogement() {
      return !!this.tempLogement;
    },

    /**
     * Méthode publique pour forcer l'initialisation des structures de données
     * Peut être appelée depuis les composants en cas de problème
     */
    forceInitialization() {
      this._initializeOptimizations();
    },

    /**
     * Vérifie si les données persistées sont expirées
     * @param {number} maxAge - Âge maximum en millisecondes (défaut: 10 minutes)
     * @returns {boolean} True si les données sont expirées
     */
    isPersistedDataExpired(maxAge = 10 * 60 * 1000) {
      if (!this._lastLoadTimestamp) return true;
      const age = Date.now() - this._lastLoadTimestamp;
      return age > maxAge;
    },

    // 🎯 Recherche géospatiale par rayon
    async searchByLocation({
      latitude,
      longitude,
      radius = 1000,
      placeData = null,
      page,
      limit,
      date,
    }) {
      try {
        this.selectedPlace = placeData;
        this.searchIsActive = true;
        this.inProgress = true;

        // ✅ Extraire les filtres applicables (exclure _geospatial_search et geolocalisation)
        const filtersForGeo = this._getFiltersForGeospatialSearch();

        const result = await LogementService.searchByLocation({
          latitude,
          longitude,
          radius,
          date,
          filters: filtersForGeo,
          page: page || this.page,
          limit: limit || this.limit,
          fields: this.fields,
          sort: this.sort,
          deep: this.deep,
        });

        // ✅ Réinitialiser la page à 1 pour une nouvelle recherche
        const searchPage = page || 1;
        this.page = searchPage;

        // ✅ Mettre à jour limit si fourni (important pour hasMoreItems)
        if (limit) {
          this.limit = limit;
        }

        // Mise à jour du store
        if (result && result.data) {
          // Si c'est la première page, remplacer les items, sinon on utilise loadMoreByLocation
          if (searchPage === 1) {
            this.items = result.data;
            // Réinitialiser les IDs chargés pour une nouvelle recherche
            this._loadedIds = new Set(result.data.map((item) => item.id));
          } else {
            // Ne devrait pas arriver ici, mais au cas où
            this.items = result.data;
          }

          // ✅ Utiliser meta.filter_count comme source de vérité (peut être string ou number)
          const receivedCount = result.data.length;
          const metaCount = parseInt(result.meta?.filter_count || 0, 10);

          // Utiliser meta.filter_count directement comme totalCount
          this.totalCount = metaCount;
          this._totalCount = metaCount;

          this.filters = {
            ...this._filters,
            _geospatial_search: {
              latitude,
              longitude,
              radius,
              date,
            },
          };

          // ✅ Mettre à jour le timestamp du dernier chargement
          this._lastLoadTimestamp = Date.now();
        } else {
          this.items = [];
          this.totalCount = 0;
          this._totalCount = 0;
        }

        this.inProgress = false;
        return result;
      } catch (error) {
        this.inProgress = false;
        this.searchIsActive = false;
        throw error;
      }
    },
    // 🎯 Charger plus de résultats pour une recherche géospatiale
    async loadMoreByLocation({
      latitude,
      longitude,
      radius = 1000,
      placeData = null,
      date = null,
    }) {
      try {
        // 🔍 Vérifications précoces optimisées
        if (
          !this.hasMoreItems ||
          this._loadingStates.loadMore ||
          this.inProgress
        ) {
          return false;
        }

        this._initializeOptimizations();

        // ✅ Récupérer les dates depuis les filtres si non fournies
        const searchDate =
          date || this.filters?._geospatial_search?.date || null;

        const currentPage = this.page;
        const nextPage = this.page + 1;
        this._loadingStates.loadMore = true;
        this.inProgress = true;

        const filtersForGeo = this._getFiltersForGeospatialSearch();

        const result = await LogementService.searchByLocation({
          latitude,
          longitude,
          radius,
          date: searchDate,
          filters: filtersForGeo,
          page: nextPage,
          limit: this.limit,
          fields: this.fields,
          sort: this.sort,
          deep: this.deep,
        });

        // ✅ Ajouter les nouveaux logements à la liste existante
        if (result && result.data && result.data.length > 0) {
          // 🆔 Gestion intelligente des doublons avec Set pour performance O(1)
          const currentIds = new Set(this.items.map((item) => item.id));
          const uniqueNewItems = result.data.filter(
            (item) => !currentIds.has(item.id)
          );

          // Ajouter uniquement les nouveaux items
          if (uniqueNewItems.length > 0) {
            this.items = [...this.items, ...uniqueNewItems];
            uniqueNewItems.forEach((item) => this._loadedIds?.add(item.id));

            // Mettre à jour la page
            this.page = nextPage;

            // ✅ Utiliser meta.filter_count comme source de vérité (peut être string ou number)
            const metaCount = parseInt(result.meta?.filter_count || 0, 10);
            const receivedCount = result.data.length;

            // Si on reçoit moins que limit, on a atteint la fin
            if (receivedCount < this.limit) {
              // On a atteint la fin, utiliser la taille réelle des items chargés
              this.totalCount = this.items.length;
              this._totalCount = this.items.length;
            } else {
              // Utiliser meta.filter_count comme totalCount (il y a encore des pages)
              this.totalCount = metaCount;
              this._totalCount = metaCount;

              // ✅ Vérifier si on a chargé tous les items disponibles (cas où items.length >= metaCount)
              if (this.items.length >= metaCount) {
                // On a tout chargé, ajuster le totalCount à la taille réelle
                this.totalCount = this.items.length;
                this._totalCount = this.items.length;
              }
            }

            this.inProgress = false;
            this._loadingStates.loadMore = false;

            return true;
          }

          // Pas de nouveaux items (tous sont des doublons)
          this.inProgress = false;
          this._loadingStates.loadMore = false;
          return false;
        }

        // Aucun résultat - on a atteint la fin, ajuster le totalCount
        // Si on a des items, utiliser leur longueur, sinon vérifier meta.filter_count
        if (result && result.meta?.filter_count) {
          const metaCount = parseInt(result.meta.filter_count, 10);
          this.totalCount = Math.min(metaCount, this.items.length);
          this._totalCount = Math.min(metaCount, this.items.length);
        } else {
          this.totalCount = this.items.length;
          this._totalCount = this.items.length;
        }
        this.inProgress = false;
        this._loadingStates.loadMore = false;
        return false;
      } catch (error) {
        this.inProgress = false;
        this._loadingStates.loadMore = false;
        this.searchIsActive = false;
        return false;
      }
    },

    // ===== Sélection et projection de mode (PUBLIC vs AUTH) =====
    _detectMode(explicitIsPublic) {
      if (typeof explicitIsPublic === "boolean") {
        const mode = explicitIsPublic ? "public" : "auth";
        return mode;
      }
      // ✅ Utiliser SessionStorageService pour vérifier l'authentification
      const hasToken = SessionStorageService.hasSession();

      if (!hasToken) {
        return "public";
      }

      // Le token existe, on vérifie le rôle
      try {
        const sessionUser = SessionStorageService.getUser();
        const roleName = sessionUser?.role?.name;

        if (roleName === "Voyageur") {
          return "auth"; // Mode AUTH seulement pour les voyageurs
        } else {
          return "public"; // Les autres rôles voient la version publique
        }
      } catch (e) {
        return "public"; // En cas d'erreur, fallback sécurisé en public
      }
    },

    _applyMode(mode) {
      this.currentMode = mode;
      if (mode === "public") {
        this.items = this.publicItems || [];
        this.meta = this.publicMeta || {};
        this.totalCount = this.publicTotalCount || 0;
      } else {
        this.items = this.authItems || [];
        this.meta = this.authMeta || {};
        this.totalCount = this.authTotalCount || 0;
      }
    },

    _storeResultForMode(mode, result) {
      const data = result?.data || [];
      const meta = result?.meta || {};
      const count =
        typeof result?.totalCount === "number"
          ? result.totalCount
          : meta?.filter_count || 0;

      if (mode === "public") {
        this.publicItems = data;
        this.publicMeta = meta;
        this.publicTotalCount = count;
      } else {
        this.authItems = data;
        this.authMeta = meta;
        this.authTotalCount = count;
      }
      this._applyMode(mode);
    },

    /**
     * Vide uniquement les données liés à l'authentification.
     * Appelé lors de la déconnexion pour nettoyer les données utilisateur.
     */
    clearAuthData() {
      this.authItems = [];
      this.authMeta = {};
      this.authTotalCount = 0;
    },

    updateLogementFavorisStatus(logementId, isFavorited) {
      const updateItem = (item) => {
        if (item.id === logementId) {
          // On met à jour le champ 'favoris' manuellement
          item.favoris = isFavorited ? [{ id: "temp-favori" }] : [];
        }
        return item;
      };

      // On met à jour toutes les listes où le logement pourrait se trouver
      this.authItems = this.authItems.map(updateItem);
      this.publicItems = this.publicItems.map(updateItem);
      this.items = this.items.map(updateItem);
      if (this.item && this.item.id === logementId) {
        this.item.favoris = isFavorited ? [{ id: "temp-favori" }] : [];
      }
    },

    /**
     * 🏷️ Récupération des catégories de logements avec cache intelligent
     *
     * Stratégie de cache optimisée pour performance :
     * 1. Charge depuis LocalStorage pour affichage immédiat
     * 2. Vérifie en ligne (données fraîches)
     * 3. Met à jour LocalStorage si nouvelles données
     * 4. Écrase LocalStorage si serveur retourne vide
     */
    async fetchCategories() {
      const CACHE_KEY = "awlo_categories_cache";
      const TTL = 5 * 60 * 1000; // 5 minutes
      const now = Date.now();

      // 1️⃣ Charger depuis LocalStorage pour affichage immédiat (client uniquement - SSR)
      if (typeof localStorage !== "undefined") {
        try {
          const cached = LocalStorage.getItem(CACHE_KEY);

          if (cached && Array.isArray(cached.data) && cached.data.length > 0) {
            const age = now - (cached.timestamp || 0);

            // Si cache valide (< 5 min), l'utiliser directement
            if (age < TTL) {
              this.categories = cached.data;
              this._lastCategoriesFetch = cached.timestamp;
              return this.categories;
            }

            // Si cache expiré mais données présentes, les afficher pendant le chargement
            if (this.categories.length === 0) {
              this.categories = cached.data;
            }
          }
        } catch (error) {
          // Ignorer silencieusement les erreurs de lecture cache
        }
      }

      // 2️⃣ Vérifier en ligne pour données fraîches
      try {
        const { getPublic } = useKy();

        const response = await getPublic("items/categorie_logement", {
          searchParams: {
            filter: JSON.stringify({ is_active: { _eq: true } }),
            sort: "-date_created",
            fields: "*",
          },
        });

        const data = response?.data || response;

        // 3️⃣ Si serveur retourne des données, mettre à jour
        if (Array.isArray(data) && data.length > 0) {
          this.categories = data;
          this._lastCategoriesFetch = now;

          // Sauvegarder dans LocalStorage (client uniquement - SSR)
          if (typeof localStorage !== "undefined") {
            try {
              LocalStorage.set(CACHE_KEY, {
                data: data,
                timestamp: now,
              });
            } catch (error) {
              // Ignorer erreurs écriture
            }
          }

          return this.categories;
        }

        // 4️⃣ Si serveur retourne vide, écraser le cache local
        if (Array.isArray(data) && data.length === 0) {
          this.categories = [];
          this._lastCategoriesFetch = now;

          if (typeof localStorage !== "undefined") {
            try {
              LocalStorage.remove(CACHE_KEY);
            } catch (error) {
              // Ignorer erreurs suppression
            }
          }

          return [];
        }

        // Si réponse invalide, garder les données en cache
        return this.categories;
      } catch (error) {
        // En cas d'erreur réseau, garder les données en cache si disponibles
        const cats = Array.isArray(this.categories) ? this.categories : [];
        return cats.length > 0 ? cats : [];
      }
    },
  },

  // 💾 Configuration de persistance - uniquement préférences utilisateur (pas de données massives)
  persist: {
    key: "awlo-logements-store",
    storage: ssrSafeLocalStorage,
    paths: [
      "filters", // ✅ Préférences de recherche utilisateur
      "sort", // ✅ Préférence de tri
      "currentMode", // ✅ Mode d'affichage
      "selectedLogementTypeId", // ✅ Type de logement sélectionné
      "selectedPlace", // ✅ Lieu sélectionné pour recherche géospatiale
      "searchIsActive", // ✅ État de recherche
      "page", // ✅ Page actuelle pour restauration
      "_lastLoadTimestamp", // ✅ Timestamp pour vérifier l'expiration
      "categories", // ✅ Persister les catégories (légères) pour affichage immédiat
      "_lastCategoriesFetch", // ✅ Timestamp pour le cache des catégories
    ],
    // Fonction pour s'assurer que les structures sont correctement initialisées après hydratation
    afterRestore: (ctx) => {
      // Ne pas restaurer liste_equipements/liste_commodites (sélections obsolètes entre sessions)
      if (ctx.store.filters && typeof ctx.store.filters === "object") {
        const { liste_equipements, liste_commodites, ...rest } =
          ctx.store.filters;
        ctx.store.filters = rest;
        if (Array.isArray(ctx.store.filters._and)) {
          ctx.store.filters._and = ctx.store.filters._and.filter(
            (f) => f && !f.liste_equipements && !f.liste_commodites
          );
        }
      }
      // Réinitialiser les structures de données qui ne peuvent pas être sérialisées
      if (
        !ctx.store._loadedIds ||
        typeof ctx.store._loadedIds.add !== "function"
      ) {
        ctx.store._loadedIds = new Set();
      }

      // ⚠️ RÉINITIALISATION EXPLICITE : S'assurer que les données volumineuses
      // ne sont JAMAIS restaurées depuis localStorage, même si elles y étaient
      // persistées dans une ancienne version du code
      ctx.store.items = [];
      ctx.store.publicItems = [];
      ctx.store.authItems = [];
      ctx.store.item = null;
      ctx.store.meta = {};
      ctx.store.publicMeta = {};
      ctx.store.authMeta = {};
      ctx.store.totalCount = 0;
      ctx.store.publicTotalCount = 0;
      ctx.store.authTotalCount = 0;
      ctx.store._totalCount = 0;
      ctx.store.tempLogement = null;

      // Réinitialiser les états de chargement
      if (
        !ctx.store._loadingStates ||
        typeof ctx.store._loadingStates !== "object"
      ) {
        ctx.store._loadingStates = {
          initialLoad: false,
          loadMore: false,
          refresh: false,
          search: false,
        };
      }
    },
  },
});

// 🎯 EXPORT PAR DÉFAUT
export default useLogementsStore;
